import React, {Fragment, useCallback, useEffect, useMemo, useReducer, useRef} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Eventcalendar, Datepicker, momentTimezone } from "@mobiscroll/react";
import moment from "moment-timezone";
import ReactModal from "react-modal";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import Select from "react-select";

import ai from "../../helpers/axios";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Loader from "../loader";
import { LoadingButton } from "../shared/loading-button";
import PeakAdjustments from "./peak-adjustments";

momentTimezone.moment = moment;

const myView = { calendar: { labels: true } };

const PEAK_VALUE_COLOR_MAP = {
  0: "#6faf82",
  1: "#4f8f64",
  2: "#ccb72b",
  3: "#e88c1f",
  4: "#d64c27",
  5: "#8b1b1b",
  default: "#a917bb",
};

const formatPeakDate = (data) => {
  const { date, peakDayCategory } = data;
  const { peakValue } = peakDayCategory || {};

  const localDate = moment(date).startOf("day").toDate();

  return {
    id: data._id,
    title: peakValue?.toString() || "N/A",
    allDay: true,
    start: localDate,
    endDate: localDate,
    color: PEAK_VALUE_COLOR_MAP[peakValue] || PEAK_VALUE_COLOR_MAP.default,
    cssClass: "text-center",
    data,
  };
};

const ImportErrors = {
  INVALID_FILE_FORMAT: 'INVALID_FILE_FORMAT',
  INVALID_ROWS: 'INVALID_ROWS',
  DUPLICATE_WITH_DB: 'DUPLICATE_WITH_DB',
  MISSING_DATES_IN_RANGE: 'MISSING_DATES_IN_RANGE',
};

const initialState = {
  isLoading: true,
  isSaving: false,
  isImporting: false,
  data: [],
  isCategoriesLoading: true,
  categories: [],
  maxDate: null,
  missingDates: null,
  selectedDate: undefined,
  isEditPeakDayModalOpen: false,
  selectedPeakDay: null,
  importValidation: {
    [ImportErrors.INVALID_FILE_FORMAT]: false,
    [ImportErrors.INVALID_ROWS]: false,
    [ImportErrors.DUPLICATE_WITH_DB]: false,
    [ImportErrors.MISSING_DATES_IN_RANGE]: false,
  },
  importErrors: {
    [ImportErrors.INVALID_ROWS]: [],
    [ImportErrors.DUPLICATE_WITH_DB]: [],
    [ImportErrors.MISSING_DATES_IN_RANGE]: [],
  },
  importFile: null,
  lastUsedCalendarArgs: {},
};

const reducer = (state, action) => {
  switch (action.type) {
    case "peakDatesSummaryDownloaded": {
      const { maxDate, missingDates } = action.data;

      return {
        ...state,
        maxDate: maxDate ? moment(maxDate).format("M/D/YYYY") : null,
        missingDates,
      };
    }

    case "peakDatesCategoriesDownloaded": {
      const { data } = action.data;

      return {
        ...state,
        isCategoriesLoading: false,
        categories: data,
      };
    }

    case "peakDatesCategoryUpdated": {
      return {
        ...state,
        categories: state.categories.map(
          (item) => item._id === action?.data?._id ?
            {
              ...item,
              adjustment: action.data.adjustment,
            } :
            item,
        ),
      };
    }

    case "peakDatesDataDownloaded": {
      const { firstDay, lastDay, data } = action.data;

      return {
        ...state,
        data: Array
          .from(
            { length: moment(lastDay).diff(moment(firstDay), "days") + 1 },
            (_, i) => moment(firstDay).add(i, "days"),
          )
          .map(date => {
            const existing = data.find((peakDate) =>
              moment(peakDate.date).isSame(date, "day"),
            );

            return formatPeakDate(existing || { date: date.format("YYYY-MM-DD") });
          }),
      };
    }

    case "peakDateCreated": {
      const [date] = action.data.date.split("T");

      return {
        ...state,
        data: [...state.data.filter(({ data }) => data.date !== date), formatPeakDate(action.data)],
      };
    }

    case "peakDateUpdated": {
      return {
        ...state,
        data: state.data.map((item) => item.id === action?.data?._id ? formatPeakDate(action.data) : item),
      };
    }

    case "selectedPeakDayFieldChanged": {
      return {
        ...state,
        selectedPeakDay: {
          ...(state.selectedPeakDay || {}),
          [action.field]: action.value,
        },
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "importValidation": {
      return {
        ...state,
        importValidation: {
          ...initialState.importValidation,
          [action.field]: action.value,
        },
      };
    }

    case "setImportErrors": {
      return {
        ...state,
        importErrors: {
          ...initialState.importErrors,
          [action.field]: action.value,
        }
      }
    }

    case "resetImportData": {
      return {
        ...state,
        importValidation: initialState.importValidation,
        importErrors: initialState.importErrors,
        importFile: null,
      }
    }

    default:
      break;
  }
  return state;
};

const PeakDaysContent = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  const inputRef = useRef(null);

  useEffect(() => {
    document.title = "Peak Days | evoJets HQ";
  }, []);

  const getPeakDatesSummary = useCallback(async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const response = await ai
      .auth(token)
      .get(`/api/peakDays/summary`)
      .catch((error) => {
        console.error(error.response.data);
        Sentry.captureException(error);
      });

    if (response?.data) {
      dispatch({
        type: "peakDatesSummaryDownloaded",
        data: response.data,
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const getPeakDatesCategories = useCallback(async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const response = await ai
      .auth(token)
      .get(`/api/peakDays/categories`)
      .catch((error) => {
        console.error(error.response.data);
        Sentry.captureException(error);
      });

    if (response?.data) {
      dispatch({
        type: "peakDatesCategoriesDownloaded",
        data: response.data,
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const getPeakDatesData = useCallback(async (args) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    dispatch({
      type: "fieldChanged",
      field: "isLoading",
      value: true,
    });

    dispatch({
      type: "fieldChanged",
      field: "lastUsedCalendarArgs",
      value: args ?? {},
    });

    const { firstDay, lastDay } = args;

    const response = await ai
      .auth(token)
      .get(`/api/peakDays`, {
        params: {
          startDate: moment(firstDay).format("YYYY-MM-DD"),
          endDate: moment(lastDay).format("YYYY-MM-DD"),
        },
      })
      .catch((error) => {
        console.error(error.response.data);
        Sentry.captureException(error);
      });

    if (response?.data) {
      dispatch({
        type: "peakDatesDataDownloaded",
        data: {
          firstDay,
          lastDay,
          data: response.data.data,
        },
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "isLoading",
      value: false,
    });
  }, [getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    getPeakDatesSummary();
    getPeakDatesCategories();
  }, [getPeakDatesSummary, getPeakDatesCategories]);

  const handlePeakDateClick = useCallback(({ event }) => {
    const { data: peakDay } = event;

    dispatch({
      type: "fieldChanged",
      field: "isEditPeakDayModalOpen",
      value: true,
    });
    dispatch({
      type: "fieldChanged",
      field: "selectedPeakDay",
      value: peakDay,
    });
  }, []);

  const handleCloseEditPeakDayModal = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "isEditPeakDayModalOpen",
      value: false,
    });
    dispatch({
      type: "fieldChanged",
      field: "selectedPeakDay",
      value: null,
    });
  }, []);

  const handlePeakDateSubmit = useCallback(async () => {
    if (!state.selectedPeakDay) {
      return;
    }
    if (!state.selectedPeakDay.peakDayCategoryId) {
      return toast.error(
        "Please fill in all required fields and retry.",
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const { _id, date, peakDayCategoryId } = state.selectedPeakDay;

    if (_id) {
      const response = await ai
        .auth(token)
        .put(`/api/peakDays/${_id}`, {
          peakDayCategoryId,
        })
        .catch((error) => {
          console.error(error.response.data);
          Sentry.captureException(error);
        });

      if (response.data) {
        dispatch({
          type: "peakDateUpdated",
          data: response?.data,
        });
      }
    } else {
      const response = await ai
        .auth(token)
        .post(`/api/peakDays`, {
          date: moment(date).format("MM/DD/YYYY"),
          peakDayCategoryId,
        })
        .catch((error) => {
          console.error(error.response.data);
          Sentry.captureException(error);
        });

      if (response.data) {
        dispatch({
          type: "peakDateCreated",
          data: response?.data,
        });
      }
    }

    await getPeakDatesSummary();

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: false,
    });
    handleCloseEditPeakDayModal();
  }, [
    state.selectedPeakDay,
    getAccessTokenSilently,
    loginWithRedirect,
    getPeakDatesSummary,
    handleCloseEditPeakDayModal,
  ]);

  const handlePeakDateCategorySubmit = useCallback(async (category) => {
    if (!category.adjustment && category.adjustment !== 0) {
      toast.error(
        "Please fill in all required fields and retry.",
      );
      return;
    }

    if (category.adjustment < 0) {
      toast.error(
        "Please enter a valid value.",
      );
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const response = await ai
      .auth(token)
      .put(`/api/peakDays/categories/${category._id}`, {
        adjustment: category.adjustment,
      })
      .catch((error) => {
        console.error(error.response.data);
        Sentry.captureException(error);
      });

    if (response.data) {
      dispatch({
        type: "peakDatesCategoryUpdated",
        data: response?.data,
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: false,
    });

    return response.data;
  }, [
    getAccessTokenSilently,
    loginWithRedirect,
  ]);

  const handleScrollToMissingDate = useCallback((event) => {
    event.preventDefault();

    if (state.missingDates?.length > 0) {
      dispatch({
        type: "fieldChanged",
        field: "selectedDate",
        value: moment.tz(state.missingDates[0], "YYYY-MM-DD", "America/New_York")
          .utc()
          .toDate(),
      });
    }
  }, [state.missingDates]);

  const peakDayCategoryOptions = useMemo(() => {
    if (!state.categories?.length) {
      return [];
    }

    return state.categories.map(({ _id, peakValue, adjustment }) => ({
      value: _id,
      label: `${peakValue} - ${(adjustment * 100).toFixed(2)}%`,
    }));
  }, [state.categories]);

  const handleOpenImportFileSelect = useCallback((event) => {
    event.preventDefault();

    if (inputRef.current) {
      inputRef.current.click();
    }
  }, []);

  const handleImport = useCallback(async (event, { force = false, ignoredError } = {}) => {
    const fileUploaded = event.target.files[0];
    event.target.value = null;

    if (!fileUploaded || fileUploaded.type !== "text/csv") {
      dispatch({
        type: "importValidation",
        field: ImportErrors.INVALID_FILE_FORMAT,
        value: true,
      });
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isImporting",
      value: true,
    });

    const token = await getAccessTokenSilently().catch(() => {
      return loginWithRedirect();
    });

    const formData = new FormData();
    formData.append("attachments[]", fileUploaded);

    if (force) {
      formData.append("force", true);
      formData.append("ignoredError", ignoredError);
    }

    const response = await ai
      .auth(token)
      .post("/api/peakDays/import", formData)
      .catch(() => {
      });

    dispatch({
      type: "fieldChanged",
      field: "isImporting",
      value: false,
    });

    if (response?.data?.success) {
      toast.success("Import is completed");
      dispatch({
        type: "resetImportData",
      });
      await getPeakDatesData(state.lastUsedCalendarArgs);
      return;
    }

    if (response?.data?.errorCode) {
      dispatch({
        type: "fieldChanged",
        field: "importFile",
        value: fileUploaded,
      });

      switch (response.data.errorCode) {
        case ImportErrors.INVALID_ROWS:
          dispatch({
            type: "importValidation",
            field: ImportErrors.INVALID_ROWS,
            value: true,
          });
          dispatch({
            type: "setImportErrors",
            field: ImportErrors.INVALID_ROWS,
            value: response.data.errors ?? [],
          });
          break;
        case ImportErrors.DUPLICATE_WITH_DB:
          dispatch({
            type: "importValidation",
            field: ImportErrors.DUPLICATE_WITH_DB,
            value: true,
          });
          break;
        case ImportErrors.MISSING_DATES_IN_RANGE:
          dispatch({
            type: "importValidation",
            field: ImportErrors.MISSING_DATES_IN_RANGE,
            value: true,
          });
          dispatch({
            type: "setImportErrors",
            field: ImportErrors.MISSING_DATES_IN_RANGE,
            value: response.data.errors ?? [],
          });
          break;
        case ImportErrors.INVALID_FILE_FORMAT:
          dispatch({
            type: "importValidation",
            field: ImportErrors.INVALID_FILE_FORMAT,
            value: true,
          });
          break;
        default:
          toast.error("Something went wrong");
      }
    }
  }, [getAccessTokenSilently, getPeakDatesData, loginWithRedirect, state.lastUsedCalendarArgs]);

  const handleImportForce = useCallback((ignoredError) => () => {
    dispatch({
      type: "resetImportData",
    });

    handleImport({
      target: {
        files: [state.importFile],
      },
    }, {force: true, ignoredError});
  }, [handleImport, state.importFile]);

  const handleCloseImportValidationModal = useCallback(() => {
    dispatch({
      type: "resetImportData",
    })
  }, []);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1="Peak Days"
        pretitle="Calculator"
        breadcrumbs={[{ link: "/peak-days", label: "Peak Days" }]}
        extraClasses="page-header-trips-l1"
        rightColumn={
          <div className="header-actions-row mr-0">
            <LoadingButton
              type="button"
              className="base-button evo-blue"
              title="Import Peak Dates"
              onClick={handleOpenImportFileSelect}
              isLoading={state.isImporting && !Object.values(state.importValidation).filter(Boolean).length}
            >
              Import
            </LoadingButton>
            <input className="hidden" ref={inputRef} type="file" accept=".csv" onChange={handleImport}/>
          </div>
        }
      />

      <div className="mt-20 position-relative">
        <div className="peak-dates-grid">
          <div className="position-relative">
            {state.isLoading && <Loader additionalClasses="position-absolute top-50 start-50 translate-middle z-25" />}

            <Eventcalendar
              className="peak-dates-calendar"
              clickToCreate={false}
              dragToCreate={false}
              dragToMove={false}
              refreshControl={false}
              dragToResize={false}
              eventDelete={false}
              showEventTooltip={false}
              onPageLoading={getPeakDatesData}
              onEventClick={handlePeakDateClick}
              data={state.data}
              selectedDate={state.selectedDate}
              view={myView}
              theme="ios"
              themeVariant="light"
              fuseeffect
              dataTimezone="UTC"
              displayTimezone="local"
              renderMode="month"
              calendarScroll="month"
              calendarType="month"
            />

            {state.maxDate && (
              <div className="mt-20 font-weight-bold">
                The last date for which Peak value is entered is {state.maxDate}
              </div>
            )}

            {state.missingDates && state.maxDate && (
              state.missingDates.length > 0 ? (
                <div className="mt-20 font-weight-bold">
                  Peak value is missing for some dates between today and {state.maxDate}.
                  Click
                  <button
                    className="peak-dates-calendar-link-button"
                    onClick={handleScrollToMissingDate}
                  >
                    &nbsp;here&nbsp;
                  </button>
                  to automatically scroll calendar to the first date without Peak value.
                </div>
              ) : (
                <div className="mt-20 font-weight-bold">
                  All dates between today and {state.maxDate} are filled in with Peak value
                </div>
              ))}
          </div>

          <div>
            <PeakAdjustments
              isLoading={state.isCategoriesLoading}
              data={state.categories}
              isSaving={state.isSaving}
              handleUpdate={handlePeakDateCategorySubmit}
            />
          </div>
        </div>
      </div>

      <ReactModal
        isOpen={state.isEditPeakDayModalOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseEditPeakDayModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "400px",
            maxHeight: "80vh",
            minWidth: "calc(min(400px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header edit-note-modal">
          <h4>{state.selectedPeakDay?._id ? "Edit" : "Add"} Peak Date</h4>
        </div>

        <div className="modal-body edit-note-modal">
          <form className="w-100 base-form mt-10">
            <div className="datepickerContainer mb-30">
              <label htmlFor="date" className="base-label">
                Date <span className="required">*</span>
              </label>

              <Datepicker
                value={state.selectedPeakDay?.date}
                dataTimezone="utc"
                displayTimezone="local"
                timezonePlugin={momentTimezone}
                controls={["date"]}
                disabled
              />
            </div>

            <div className="form-group">
              <label htmlFor="selectedPeakDayCategory" className="base-label">
                Peak Value <span className="required">*</span>
              </label>

              <Select
                className="selectedPeakDayCategory-select"
                classNamePrefix="select"
                options={peakDayCategoryOptions}
                placeholder="Peak Value"
                onChange={(selectedValue) => {
                  dispatch({
                    type: "selectedPeakDayFieldChanged",
                    field: "peakDayCategoryId",
                    value: selectedValue?.value,
                  });
                }}
                key={`key-selected-peak-day-category`}
                value={peakDayCategoryOptions?.find(({ value }) => value === state.selectedPeakDay?.peakDayCategoryId)}
                name="selectedPeakDayCategory"
                required
                menuPosition="fixed"
              />
            </div>
          </form>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <LoadingButton
            className="base-button evo-blue flex-half"
            type="button"
            onClick={handlePeakDateSubmit}
            isLoading={state.isSaving}
          >
            Submit
          </LoadingButton>

          <button
            className="base-button btn-danger flex-half"
            type="button"
            onClick={handleCloseEditPeakDayModal}
            disabled={state.isSaving}
          >
            Cancel
          </button>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.importValidation[ImportErrors.INVALID_FILE_FORMAT]}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseImportValidationModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Invalid import file</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            Selected file is not valid for Date's Peak import.
          </div>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20 d-flex">
            <button
              className="base-button evo-blue flex-grow-1"
              type="button"
              onClick={handleCloseImportValidationModal}
            >
              Close
            </button>
          </div>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.importValidation[ImportErrors.INVALID_ROWS]}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseImportValidationModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Invalid Data</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            Some rows in the file contain invalid data. Please check the rows listed below:
          </div>
          <div className="w-100 mt-20">
              {state.importErrors[ImportErrors.INVALID_ROWS].join(', ')}
          </div>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex">
            <button
              className="base-button evo-blue flex-grow-1"
              type="button"
              onClick={handleCloseImportValidationModal}
            >
              Close
            </button>
          </div>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.importValidation[ImportErrors.DUPLICATE_WITH_DB]}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseImportValidationModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Duplicated Peak Dates</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            Some dates provided in the import file already have Peak values in the database. Do you want to overwrite those dates?
          </div>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleCloseImportValidationModal}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              isLoading={state.isImporting}
              onClick={handleImportForce(ImportErrors.DUPLICATE_WITH_DB)}
            >
              Import
            </LoadingButton>
          </div>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.importValidation[ImportErrors.MISSING_DATES_IN_RANGE]}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseImportValidationModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Peak Dates Gap</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            Some dates are not provided, and this will cause gap(s) in the final Peak Dates database after the import is completed. Are you sure you want to continue?
          </div>
          <div className="w-100 mt-20">
            List of missing dates:
            <ul className="no-bullets-list pl-0">
              {state.importErrors[ImportErrors.MISSING_DATES_IN_RANGE].map((date, index) => (
                <li key={`missing-${index}-${date}`}>
                  - {moment(date).format("M/D/YY")}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleCloseImportValidationModal}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              isLoading={state.isImporting}
              onClick={handleImportForce(ImportErrors.MISSING_DATES_IN_RANGE)}
            >
              Import
            </LoadingButton>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default PeakDaysContent;
